import { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useParams } from 'react-router';
import { FormSpec, LegalEntityRegion } from '../types';
import {
  Section,
  SectionHeading,
  Button,
  BatchApiStatusHandler,
  BlockAlert,
  AlertVariants,
} from '../components';
import { Renderer, formSpecToRendererConfig } from '../components/forms';
import { useBatchApi } from '../hooks';
import { fetchApi } from '../utils/fetchApi';
import { ApiStatus } from '../utils/types';
import { useSearchParams } from 'react-router-dom';
import { safelyUpdateLocation } from '../utils/redirect';

interface LocationCriteriaImportProps {
  location: LegalEntityRegion;
}

export const LocationCriteriaImport = ({
  location,
}: LocationCriteriaImportProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const callbackUrl = searchParams.get('callback_url');

  const formFields = [
    {
      name: 'has_employees',
      label: `Do you have employees in ${location.region.name}`,
      component: { type: 'check' },
    },
    {
      name: 'has_physical_location',
      label: `Do you have a physical location in ${location.region.name}`,
      component: { type: 'check' },
    },
  ];

  /* eslint-disable camelcase */
  const loadedSpec: FormSpec = {
    title: `${location.region.name} Configuration`,
    sections: [{ form_fields: formFields }],
  };
  /* eslint-enable camelcase */

  const formMethods = useForm();
  const {
    handleSubmit,
    clearErrors,
    setError,
    formState: { errors },
  } = formMethods;

  const onSubmit = async (data: any) => {
    clearErrors();

    if (errors.isNotEmpty) {
      return;
    }

    const configurationResponse = await fetchApi({
      url: `/api/legal_entity/locations/${location.region.code.toLowerCase()}/configuration`,
      method: 'PATCH',
      body: data,
    });

    if (configurationResponse.status === ApiStatus.Error) {
      setError('submit', {
        type: 'server-error',
        message: "Something went wrong, we're looking into it.",
      });
      setIsLoading(false);

      return;
    }

    if (callbackUrl) {
      safelyUpdateLocation(callbackUrl);
    }
  };

  return (
    <Section className="flex flex-col items-center">
      <SectionHeading className="w-96 pb-4" text={loadedSpec.title} />

      <FormProvider {...formMethods}>
        <form className="my-8 w-96" onSubmit={handleSubmit(onSubmit)}>
          <Renderer
            config={formSpecToRendererConfig(loadedSpec)}
            errors={errors}
          />

          <div className="space-y-4">
            {errors.submit && (
              <BlockAlert variant={AlertVariants.Error}>
                {typeof errors.submit.message === 'string'
                  ? errors.submit.message
                  : 'Something went wrong.'}
              </BlockAlert>
            )}

            <Button
              type="submit"
              size="large"
              isLoading={isLoading}
              isFullWidth
            >
              Submit
            </Button>
          </div>
        </form>
      </FormProvider>
    </Section>
  );
};

export const LocationCriteriaImportView = () => {
  const { regionCode } = useParams();
  const [searchParams] = useSearchParams();
  const callbackUrl = searchParams.get('callback_url');

  if (!regionCode) {
    if (callbackUrl) {
      safelyUpdateLocation(callbackUrl);
    }

    throw Error('Location ID and callback URL missing.');
  }

  const batchResponse = useBatchApi([
    {
      url: `/api/legal_entity/locations/${regionCode}`,
      method: 'GET',
    },
  ]);

  const componentPropsFn = ([
    reqResponse,
  ]: any[]): LocationCriteriaImportProps => {
    return {
      location: reqResponse,
    };
  };

  return (
    <BatchApiStatusHandler
      batchResponse={batchResponse}
      component={LocationCriteriaImport}
      componentProps={componentPropsFn}
    />
  );
};
